import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  parseEmbroideryDetails,
  mapEmbroideryDetails,
} from '../../utils/embroidery';

import {
  EmbroideryLine,
  DetailText,
  CustomizationText,
  TextCustomizations,
  LogoCustomizations,
} from './components';

const EmbroideryDetailsContainer = styled.div`
  margin: 8px 0;

  ${EmbroideryLine} {
    ${({ theme, detailLineItemLabelStyles }) => css`
      color: ${theme.colors.textFadedAlternate};

      ${detailLineItemLabelStyles};
    `}

  ${DetailText} {
    ${({ detailLineItemValueStyles }) => detailLineItemValueStyles}
  }

  ${CustomizationText} {
    ${({ customizationTextStyles, detailLineItemValueStyles }) =>
      customizationTextStyles || detailLineItemValueStyles}
  }
`;

const Spacer = styled.div`
  height: 8px;
`;

const ItemEmbroideryDetails = ({
  customizationTextStyles,
  detailLineItemLabelStyles,
  detailLineItemValueStyles,
  textPersonalizations,
  iconPersonalizations,
  personalizations,
  separatorCmp,
  embroideryConfigs,
}) => {
  const customizations = useMemo(() => {
    const parsedDetails = parseEmbroideryDetails(personalizations);
    return iconPersonalizations && textPersonalizations
      ? mapEmbroideryDetails(
          iconPersonalizations,
          textPersonalizations,
          parsedDetails
        )
      : parsedDetails;
  }, [iconPersonalizations, personalizations, textPersonalizations]);

  const hasLogo = !!customizations.logo;
  const hasText = !!(customizations.line1 || customizations.line2);

  return (
    <EmbroideryDetailsContainer
      detailLineItemLabelStyles={detailLineItemLabelStyles}
      detailLineItemValueStyles={detailLineItemValueStyles}
      customizationTextStyles={customizationTextStyles}
    >
      <TextCustomizations
        textCustomizations={customizations}
        embroideryConfigs={embroideryConfigs}
      />
      {hasLogo && hasText && (separatorCmp || <Spacer />)}
      <LogoCustomizations logoCustomizations={customizations} />
    </EmbroideryDetailsContainer>
  );
};

ItemEmbroideryDetails.propTypes = {
  customizationTextStyles: PropTypes.string,
  detailLineItemLabelStyles: PropTypes.string,
  detailLineItemValueStyles: PropTypes.string,
  embroideryConfigs: PropTypes.array,
  iconPersonalizations: PropTypes.shape({
    productId: PropTypes.number,
    type: PropTypes.string,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        locationId: PropTypes.oneOf([1, 2]),
        label: PropTypes.oneOf(['Left Sleeve', 'Right Sleeve']),
        customizations: PropTypes.arrayOf(
          PropTypes.shape({
            customizationId: 'logo',
            label: 'logo',
            options: PropTypes.arrayOf(
              PropTypes.shape({
                optionId: PropTypes.number,
                label: PropTypes.string,
              })
            ),
          })
        ),
      })
    ),
  }),
  personalizations: PropTypes.arrayOf(
    PropTypes.shape({
      customizations: PropTypes.arrayOf(
        PropTypes.shape({
          customizationId: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      locationId: PropTypes.number,
      locationLabel: PropTypes.string,
    })
  ),
  separatorCmp: PropTypes.node,
  textPersonalizations: PropTypes.shape({
    productId: PropTypes.number,
    type: PropTypes.string,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        locationId: PropTypes.number,
        label: PropTypes.string,
        customizations: PropTypes.arrayOf(
          PropTypes.shape({
            customizationId: 'text',
            label: 'text',
            options: PropTypes.arrayOf(
              PropTypes.shape({
                optionId: PropTypes.number,
                label: PropTypes.string,
              })
            ),
          })
        ),
      })
    ),
  }),
};

export default ItemEmbroideryDetails;
